import React, { createContext, useContext, useState, ReactNode, useEffect } from 'react';

// Define environment types
export type Environment = 'development' | 'production';

// Define context type
interface EnvContextType {
  isDev: boolean;
  toggleDevMode: () => void;
  isActualDevEnvironment: boolean;
}

// Determine if we're in actual development environment
// Check both NODE_ENV and our custom REACT_APP_ENV
const isActualDevelopmentEnvironment = () => {
  // For production builds, NODE_ENV will be 'production' and DevTools won't be available
  if (process.env.NODE_ENV === 'production') {
    return false;
  }
  
  // For local development, we can toggle between dev/prod using REACT_APP_ENV
  // This allows testing production-like environment while still in dev mode
  return process.env.REACT_APP_ENV !== 'production';
};

// Create context with default values
const EnvContext = createContext<EnvContextType>({
  isDev: false,
  toggleDevMode: () => {},
  isActualDevEnvironment: isActualDevelopmentEnvironment()
});

// Custom hook to use the environment context
export const useEnv = () => useContext(EnvContext);

// Props for the provider component
interface EnvProviderProps {
  children: ReactNode;
}

/**
 * Environment Provider component that manages application environment state
 */
export const EnvProvider: React.FC<EnvProviderProps> = ({ children }) => {
  // Check if we're actually in development environment
  const isActualDevEnvironment = isActualDevelopmentEnvironment();
  
  // Default to production mode, but check localStorage if available
  // In production builds, always force production mode
  const [isDev, setIsDev] = useState(() => {
    if (!isActualDevEnvironment) {
      return false; // Always false in production builds
    }
    
    if (typeof window !== 'undefined' && window.localStorage) {
      const savedMode = localStorage.getItem('smuglerDevMode');
      return savedMode === 'true';
    }
    return false;
  });

  // Force production mode when running in production environment
  useEffect(() => {
    if (!isActualDevEnvironment && isDev) {
      setIsDev(false);
    }
  }, [isActualDevEnvironment, isDev]);

  const toggleDevMode = () => {
    // Only allow toggling in actual development environment
    if (!isActualDevEnvironment) return;
    
    setIsDev(prev => {
      const newMode = !prev;
      // Save to localStorage if available
      if (typeof window !== 'undefined' && window.localStorage) {
        localStorage.setItem('smuglerDevMode', String(newMode));
      }
      return newMode;
    });
  };

  return (
    <EnvContext.Provider value={{ isDev, toggleDevMode, isActualDevEnvironment }}>
      {children}
    </EnvContext.Provider>
  );
};

export default EnvProvider; 